import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    Shield, 
    Download, 
    Calendar,
    Clock,
    User,
    ChevronLeft,
    ChevronRight,
    Search,
    Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, subMonths, addMonths } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DutyOfficerTimecardPage() {
    const navigate = useNavigate();
    const { user, hasUnitAccess, hasGroupAccess } = useAuth();
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [searchTerm, setSearchTerm] = useState('');
    const [stats, setStats] = useState({
        totalShifts: 0,
        totalHours: 0,
        uniqueOfficers: 0
    });

    useEffect(() => {
        fetchDutyOfficerRecords();
    }, [currentMonth]);

    const fetchDutyOfficerRecords = async () => {
        setLoading(true);
        try {
            const startDate = startOfMonth(currentMonth).toISOString();
            const endDate = endOfMonth(currentMonth).toISOString();
            
            // Fetch attendance records where is_duty_officer is true
            const response = await axios.get(`${API_URL}/attendance/duty-officer-records`, {
                params: { start_date: startDate, end_date: endDate }
            });
            
            const data = response.data || [];
            setRecords(data);
            
            // Calculate stats
            const totalHours = data.reduce((sum, record) => {
                if (record.clock_out) {
                    const clockIn = new Date(record.clock_in);
                    const clockOut = new Date(record.clock_out);
                    return sum + (clockOut - clockIn) / (1000 * 60 * 60);
                }
                return sum;
            }, 0);
            
            const uniqueOfficers = new Set(data.map(r => r.user_id)).size;
            
            setStats({
                totalShifts: data.length,
                totalHours: totalHours.toFixed(1),
                uniqueOfficers
            });
        } catch (error) {
            console.error('Failed to fetch duty officer records:', error);
            // If the endpoint doesn't exist yet, show empty state
            setRecords([]);
            setStats({ totalShifts: 0, totalHours: 0, uniqueOfficers: 0 });
        } finally {
            setLoading(false);
        }
    };

    const handleExportCSV = () => {
        if (records.length === 0) {
            toast.error('No records to export');
            return;
        }

        const headers = ['Date', 'Officer Name', 'Clock In', 'Clock Out', 'Duration (Hours)', 'Activity'];
        const csvData = records.map(record => {
            const clockIn = new Date(record.clock_in);
            const clockOut = record.clock_out ? new Date(record.clock_out) : null;
            const duration = clockOut ? ((clockOut - clockIn) / (1000 * 60 * 60)).toFixed(2) : 'Active';
            
            return [
                format(clockIn, 'yyyy-MM-dd'),
                record.user_name || 'Unknown',
                format(clockIn, 'HH:mm'),
                clockOut ? format(clockOut, 'HH:mm') : 'Active',
                duration,
                record.activity_name || '-'
            ].join(',');
        });

        const csv = [headers.join(','), ...csvData].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `duty-officer-log-${format(currentMonth, 'yyyy-MM')}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Duty Officer log exported');
    };

    const previousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
    const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

    const filteredRecords = records.filter(record => 
        record.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.activity_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatDuration = (clockIn, clockOut) => {
        if (!clockOut) return 'Active';
        const start = new Date(clockIn);
        const end = new Date(clockOut);
        const hours = Math.floor((end - start) / (1000 * 60 * 60));
        const minutes = Math.floor(((end - start) % (1000 * 60 * 60)) / (1000 * 60));
        return `${hours}h ${minutes}m`;
    };

    if (!hasUnitAccess() && !hasGroupAccess()) {
        return (
            <div className="p-6">
                <Card>
                    <CardContent className="p-6 text-center">
                        <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                        <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
                        <p className="text-muted-foreground">
                            You don't have permission to view the Duty Officer log.
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="duty-officer-timecard-page">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Shield className="w-7 h-7 text-purple-600" />
                        Duty Officer Log
                    </h2>
                    <p className="text-muted-foreground mt-1">
                        Track and manage Duty Officer shift records separately from standard timecards
                    </p>
                </div>
                <Button onClick={handleExportCSV} variant="outline" data-testid="export-do-csv">
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Total Shifts</p>
                                <p className="text-3xl font-bold">{stats.totalShifts}</p>
                            </div>
                            <div className="p-3 rounded-xl bg-purple-100 dark:bg-purple-900/30">
                                <Shield className="w-6 h-6 text-purple-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Total Hours</p>
                                <p className="text-3xl font-bold">{stats.totalHours}</p>
                            </div>
                            <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-900/30">
                                <Clock className="w-6 h-6 text-blue-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Unique Officers</p>
                                <p className="text-3xl font-bold">{stats.uniqueOfficers}</p>
                            </div>
                            <div className="p-3 rounded-xl bg-amber-100 dark:bg-amber-900/30">
                                <User className="w-6 h-6 text-amber-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Month Navigation and Search */}
            <Card>
                <CardHeader className="pb-4">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="icon" onClick={previousMonth}>
                                <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <div className="flex items-center gap-2 px-4">
                                <Calendar className="w-4 h-4 text-muted-foreground" />
                                <span className="font-semibold">{format(currentMonth, 'MMMM yyyy')}</span>
                            </div>
                            <Button variant="outline" size="icon" onClick={nextMonth}>
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                        <div className="relative w-full lg:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Search officers..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9"
                                data-testid="do-search-input"
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="space-y-3">
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="h-12 bg-muted rounded animate-pulse"></div>
                            ))}
                        </div>
                    ) : filteredRecords.length === 0 ? (
                        <div className="text-center py-12">
                            <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                            <p className="text-muted-foreground">No Duty Officer records found for this period</p>
                            <p className="text-sm text-muted-foreground mt-1">
                                Records will appear here when members clock in as Duty Officer
                            </p>
                        </div>
                    ) : (
                        <div className="rounded-md border">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Officer</TableHead>
                                        <TableHead>Clock In</TableHead>
                                        <TableHead>Clock Out</TableHead>
                                        <TableHead>Duration</TableHead>
                                        <TableHead>Activity</TableHead>
                                        <TableHead>Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredRecords.map((record) => (
                                        <TableRow key={record.id}>
                                            <TableCell className="font-medium">
                                                {format(new Date(record.clock_in), 'MMM d, yyyy')}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                                                        <Shield className="w-4 h-4 text-purple-600" />
                                                    </div>
                                                    {record.user_name}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {format(new Date(record.clock_in), 'HH:mm')}
                                            </TableCell>
                                            <TableCell>
                                                {record.clock_out 
                                                    ? format(new Date(record.clock_out), 'HH:mm')
                                                    : '-'
                                                }
                                            </TableCell>
                                            <TableCell>
                                                {formatDuration(record.clock_in, record.clock_out)}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {record.activity_name || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {record.clock_out ? (
                                                    <Badge variant="secondary">Completed</Badge>
                                                ) : (
                                                    <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                                        Active
                                                    </Badge>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
